import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2, ArrowLeft, CheckCircle } from 'lucide-react'
import apiService from '../services/api'
import toast from 'react-hot-toast'

const VerifyOTP: React.FC = () => {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  const navigate = useNavigate()
  
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (!email) {
      toast.error('Email is required')
      navigate('/forgot-password')
    }
  }, [email, navigate])

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) return // Only allow single digit
    
    const newOtp = [...otp]
    newOtp[index] = value.replace(/\D/g, '') // Only allow numbers
    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newOtp = [...otp]
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pastedData[i] || ''
    }
    setOtp(newOtp)
    inputRefs.current[Math.min(pastedData.length - 1, 5)]?.focus()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email) {
      toast.error('Email is required')
      return
    }

    const otpString = otp.join('')
    if (otpString.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP')
      return
    }

    setIsLoading(true)

    try {
      const response = await apiService.verifyOTP(email, otpString)
      
      if (response.success) {
        setIsVerified(true)
        toast.success('OTP verified successfully!')
        // Redirect to reset password page with email and OTP
        setTimeout(() => {
          navigate(`/reset-password?email=${encodeURIComponent(email)}&otp=${otpString}`)
        }, 1000)
      } else {
        throw new Error(response.message || 'Invalid OTP')
      }
    } catch (error: any) {
      // Extract user-friendly error message
      let message = 'Invalid OTP. Please try again.'
      
      if (error.response?.data?.message) {
        const errorMsg = String(error.response.data.message || '').toLowerCase()
        if (errorMsg.includes('invalid') || errorMsg.includes('otp')) {
          message = 'Invalid OTP. Please check and try again.'
        } else if (errorMsg.includes('expired')) {
          message = 'OTP has expired. Please request a new one.'
        } else if (errorMsg.includes('email')) {
          message = 'Invalid email. Please try again.'
        } else {
          message = error.response.data.message
        }
      } else if (error.message) {
        message = error.message
      }
      
      toast.error(message)
      // Clear OTP on error
      setOtp(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = async () => {
    if (!email) return

    setIsLoading(true)
    try {
      const response = await apiService.forgotPassword(email)
      if (response.success) {
        toast.success('OTP resent successfully!')
        setOtp(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
      }
    } catch (error: any) {
      let message = 'Failed to resend OTP. Please try again.'
      
      if (error.response?.data?.message) {
        const errorMsg = String(error.response.data.message || '').toLowerCase()
        if (errorMsg.includes('email')) {
          message = 'Invalid email address. Please check and try again.'
        } else {
          message = error.response.data.message
        }
      } else if (error.message) {
        message = error.message
      }
      
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  if (!email) {
    return null
  }

  if (isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-primary-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              OTP Verified!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Redirecting to reset password...
            </p>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="mx-auto flex items-center justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="BookMyYogaTTC Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Enter OTP
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            We've sent a 6-digit OTP to <strong>{email}</strong>
          </p>
          <p className="mt-1 text-xs text-gray-500">
            The OTP will expire in 10 minutes
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8 space-y-6"
          onSubmit={handleSubmit}
        >
          <div className="flex justify-center gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                autoFocus={index === 0}
              />
            ))}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading || otp.join('').length !== 6}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Verify OTP'
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600">
              Didn't receive the OTP?
            </p>
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={isLoading}
              className="text-sm font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
            >
              Resend OTP
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Link>
          </div>
        </motion.form>
      </div>
    </div>
  )
}

export default VerifyOTP

